import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Backdrop,
  CircularProgress,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";

import {
  Edit,
  Loading,
  required,
  SimpleForm,
  Toolbar,
  TopToolbar,
  useEditController,
} from "react-admin";

import { useUser } from "../../../authenticatorApp/UserContext";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { FormColumns } from "../../../components/common/FormColumns";
import { FormSection } from "../../../components/common/FormSection";
import { NotesTabShow } from "../../../components/common/NotesTab";
import {
  ScheduleDateTimeInput,
  useScheduleEventType,
} from "../../../components/common/scheduler/ScheduleDateTimeInput";
import { Workflow } from "../../../utils/constants";
import { getBackPath } from "../../../utils/utils";

import { useOutboundScheduleGuard } from "../dispatchGuard";
import { DispatchSaveButton } from "./DispatchSaveButton";
import { OutboundAside } from "./OutboundAside";
import { ShipmentSetupTab } from "./OutboundShow";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const validateRequired = required();

// Shared with OutboundDispatchScheduleEdit — both carrier pickup forms save the
// schedule the same way and offer the same single action.
export const DispatchEditActions = ({ backPath, ...props }) => (
  <TopToolbar>
    <BackButton {...props} backPath={backPath} />
  </TopToolbar>
);

export const DispatchEditToolbar = ({
  record,
  backPath,
  selectedTimeslot,
  ...props
}) => {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);

  return (
    <Toolbar {...props}>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <DispatchSaveButton
        {...props}
        record={record}
        backPath={backPath}
        selectedTimeslot={selectedTimeslot}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
      />
    </Toolbar>
  );
};

/**
 * Rescheduling a pickup the carrier already holds: the same two columns as the
 * claim form — the pickup on the left to check against, the calendar on the
 * right — with the notes underneath, the one section that reads better full
 * width. The dispatch times a shipment collects on its way through the yard are
 * left out: nothing has happened to it yet while it is still only scheduled, so
 * the section had nothing to show.
 */
export const OutboundDispatchEdit = (props) => {
  const { record } = useEditController(props);
  const { appUser } = useUser();
  const history = useHistory();
  const gteMedium = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const backPath = getBackPath(props);
  const { eventType, canSchedule, selectedTimeslot, setSelectedTimeslot } =
    useScheduleEventType(
      record,
      Workflow.OUTBOUND,
      record?.account?.outboundEventTypeId,
    );
  const blocked = useOutboundScheduleGuard(record, props.basePath);

  if (blocked || !record?.account) return <Loading />;
  return (
    <Edit
      {...props}
      title={<ForceTitle title="Schedule Pickup" />}
      mutationMode="pessimistic"
      actions={<DispatchEditActions {...props} backPath={backPath} />}
      aside={<OutboundAside {...props} history={history} backPath={backPath} />}
    >
      {/* Pick list is yard-side detail; carriers never see it, here or on the
          show page. */}
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <DispatchEditToolbar
            {...props}
            record={record}
            backPath={backPath}
            selectedTimeslot={selectedTimeslot}
          />
        }
      >
        <FormColumns>
          <FormSection title="Pickup Info">
            <ShipmentSetupTab {...props} gteMedium={gteMedium} />
          </FormSection>
          <FormSection title="Schedule Pickup">
            <ScheduleDateTimeInput
              inline
              id="reschedule-input"
              label="Pickup Appointment"
              account={record.account}
              workflow={Workflow.OUTBOUND}
              eventType={eventType}
              canSchedule={canSchedule}
              selectedTimeslot={selectedTimeslot}
              setSelectedTimeslot={setSelectedTimeslot}
              validate={validateRequired}
              fullWidth={true}
              helperText={
                canSchedule
                  ? false
                  : `Contact ${appUser?.reloadYard?.name} to setup an event type.`
              }
            />
          </FormSection>
        </FormColumns>
        <FormSection title="Yard Notes">
          <NotesTabShow {...props} hidePrivate={true} hideLabel={true} />
        </FormSection>
      </SimpleForm>
    </Edit>
  );
};
